import React, { useState, useEffect, useCallback } from 'react';
import styles from './StoreVerify.module.scss';
import { normalizeToEnglish } from '../../utils/digitConverter';
import API_BASE_URL from '../../config/api';
import { useAuth } from '../../context/AuthContext.jsx';

const StoreVerify = ({ refreshTrigger = 0 }) => {
    const { makeAuthenticatedRequest } = useAuth();
    const [storeData, setStoreData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [totalItems, setTotalItems] = useState(0);

    // Filter states
    const [filters, setFilters] = useState({
        miti: '',
        dakshila_no: '',
        specification: '',
        saman_ko_pahichan: '',
        model_no: '',
        item_name: '',
        utpadan_garnedesh: '',
        size: '',
        anumit_aayu: '',
        saman_prabidhik_srot: '',
        parikshan: '',
        prati_ekai_ko_dar: '',
        jamma_paral_mulya: '',
        pariman: '',
        jamma_paral_mulya2: '',
        pariman2: '',
        jamma_paral_mulya3: '',
        kaifiyat: ''
    });

    // Verification states
    const [verificationStates, setVerificationStates] = useState({});
    const [verificationComments, setVerificationComments] = useState({});
    const [manualDepreciation, setManualDepreciation] = useState({});
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedField, setSelectedField] = useState('');

    const [showFilters, setShowFilters] = useState(false);
    const [globalSearch, setGlobalSearch] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    // Load verification states from localStorage
    useEffect(() => {
        try {
            const savedStates = localStorage.getItem('verificationStates');
            const savedComments = localStorage.getItem('verificationComments');
            const savedDepreciation = localStorage.getItem('manualDepreciation');
            if (savedStates) {
                setVerificationStates(JSON.parse(savedStates));
            }
            if (savedComments) {
                setVerificationComments(JSON.parse(savedComments));
            }
            if (savedDepreciation) {
                setManualDepreciation(JSON.parse(savedDepreciation));
            }
        } catch (error) {
            console.error('Error loading verification states:', error);
        }
    }, []);

    // Save verification states to localStorage
    const saveVerificationStates = useCallback((states, comments, depreciation) => {
        try {
            localStorage.setItem('verificationStates', JSON.stringify(states));
            localStorage.setItem('verificationComments', JSON.stringify(comments));
            localStorage.setItem('manualDepreciation', JSON.stringify(depreciation));
        } catch (error) {
            console.error('Error saving verification states:', error);
        }
    }, []);

    const fetchStoreData = useCallback(async (showLoading = true) => {
        try {
            if (showLoading) {
                setLoading(true);
            }
            const offset = (currentPage - 1) * itemsPerPage;
            const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/items/?limit=${itemsPerPage}&offset=${offset}`);
            if (response.ok) {
                const data = await response.json();
                                        setStoreData(data.results || data);
        setTotalItems(data.count || data.length);
            } else {
                setError('Failed to fetch data');
            }
        } catch (error) {
            setError('Error fetching data');
            console.error('Error:', error);
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    }, [currentPage, itemsPerPage]);

    const applyFilters = useCallback(() => {
        let filtered = [...storeData];

        // Apply global search with mixed digit support
        if (globalSearch.trim()) {
            const searchTerm = normalizeToEnglish(globalSearch.toLowerCase());
            filtered = filtered.filter(item => 
                Object.values(item).some(value => {
                    if (!value) return false;
                    const normalizedValue = normalizeToEnglish(value.toString().toLowerCase());
                    return normalizedValue.includes(searchTerm);
                })
            );
        }

        // Apply individual column filters with mixed digit support
        Object.keys(filters).forEach(key => {
            if (filters[key].trim()) {
                const filterValue = normalizeToEnglish(filters[key].toLowerCase());
                filtered = filtered.filter(item => {
                    if (!item[key]) return false;
                    const normalizedValue = normalizeToEnglish(item[key].toString().toLowerCase());
                    return normalizedValue.includes(filterValue);
                });
            }
        });

        setFilteredData(filtered);
    }, [storeData, globalSearch, filters]);

    // Apply filters whenever filters or data changes
    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    // Fetch data on component mount and when refreshTrigger changes
    useEffect(() => {
        fetchStoreData();
    }, [fetchStoreData, refreshTrigger]);

    // Calculate depreciation for an item
    const calculateDepreciation = (item) => {
        try {
            const itemId = item.id;
            const manualValue = manualDepreciation[itemId];
            
            // If manual depreciation is set, use it
            if (manualValue && manualValue.depreciationAmount !== undefined) {
                const originalValue = parseFloat(item.jamma_paral_mulya) || 0;
                const depreciationAmount = parseFloat(manualValue.depreciationAmount) || 0;
                // Use manually entered current value if available, otherwise calculate it
                const currentValue = manualValue.currentValue !== undefined && manualValue.currentValue !== '' 
                    ? parseFloat(manualValue.currentValue) || 0 
                    : Math.max(0, originalValue - depreciationAmount);
                
                return {
                    originalValue,
                    depreciationAmount: Math.round(depreciationAmount * 100) / 100,
                    currentValue: Math.round(currentValue * 100) / 100,
                    yearsDiff: manualValue.yearsDiff || 0,
                    isManual: true
                };
            }
            
            // Otherwise calculate automatically with improved precision
            const purchaseDate = new Date(item.miti_nepali);
            const currentDate = new Date();
            
            // Calculate years difference with higher precision
            const timeDiff = currentDate.getTime() - purchaseDate.getTime();
            const daysDiff = timeDiff / (1000 * 60 * 60 * 24);
            const yearsDiff = daysDiff / 365.25; // Using 365.25 for leap year accuracy
            
            // Get the original value (jamma_paral_mulya)
            const originalValue = parseFloat(item.jamma_paral_mulya) || 0;
            
            // 10% straight-line depreciation per year (keeping previous rules)
            const depreciationRate = 0.10;
            
            // Calculate depreciation with higher precision
            const depreciationAmount = originalValue * depreciationRate * yearsDiff;
            
            // Ensure current value doesn't go below 0
            const currentValue = Math.max(0, originalValue - depreciationAmount);
            
            // Round to 2 decimal places for currency precision
            const roundedDepreciationAmount = Math.round(depreciationAmount * 100) / 100;
            const roundedCurrentValue = Math.round(currentValue * 100) / 100;
            const roundedYearsDiff = Math.round(yearsDiff * 100) / 100;
            
            return {
                originalValue,
                depreciationAmount: roundedDepreciationAmount,
                currentValue: roundedCurrentValue,
                yearsDiff: roundedYearsDiff,
                isManual: false
            };
        } catch (error) {
            console.error('Error calculating depreciation:', error);
            return {
                originalValue: 0,
                depreciationAmount: 0,
                currentValue: 0,
                yearsDiff: 0,
                isManual: false
            };
        }
    };

    // Handle verification check
    const handleVerificationCheck = (itemId, isVerified) => {
        const newStates = { ...verificationStates, [itemId]: isVerified };
        setVerificationStates(newStates);
        saveVerificationStates(newStates, verificationComments, manualDepreciation);
    };

    // Handle verification comment
    const handleVerificationComment = (itemId, comment) => {
        const newComments = { ...verificationComments, [itemId]: comment };
        setVerificationComments(newComments);
        saveVerificationStates(verificationStates, newComments, manualDepreciation);
    };

    // Handle manual depreciation input
    const handleManualDepreciation = (itemId, depreciationAmount, yearsDiff, currentValue = null) => {
        // Find the item to get original value
        const item = storeData.find(item => item.id === itemId);
        const originalValue = parseFloat(item?.jamma_paral_mulya) || 0;
        
        let calculatedCurrentValue = '';
        
        // If depreciationAmount is provided and currentValue is null, auto-calculate current value
        if (depreciationAmount !== null && depreciationAmount !== '' && currentValue === null) {
            const depAmount = parseFloat(depreciationAmount) || 0;
            calculatedCurrentValue = Math.max(0, originalValue - depAmount).toString();
        } else if (currentValue !== null) {
            // If currentValue is explicitly provided, use it
            calculatedCurrentValue = currentValue;
        } else {
            // Keep existing current value if no new input
            calculatedCurrentValue = manualDepreciation[itemId]?.currentValue || '';
        }
        
        const newDepreciation = { 
            ...manualDepreciation, 
            [itemId]: { 
                depreciationAmount: depreciationAmount,
                yearsDiff: parseFloat(yearsDiff) || 0,
                currentValue: calculatedCurrentValue
            } 
        };
        setManualDepreciation(newDepreciation);
        saveVerificationStates(verificationStates, verificationComments, newDepreciation);
    };

    // Clear manual depreciation for an item
    const clearManualDepreciation = (itemId) => {
        const newDepreciation = { ...manualDepreciation };
        delete newDepreciation[itemId];
        setManualDepreciation(newDepreciation);
        saveVerificationStates(verificationStates, verificationComments, newDepreciation);
    };

    // Open field verification modal
    const openFieldVerification = (item, field) => {
        setSelectedItem(item);
        setSelectedField(field);
        setShowVerificationModal(true);
    };

    // Handle filter changes
    const handleFilterChange = (column, value) => {
        setFilters(prev => ({
            ...prev,
            [column]: value
        }));
    };

    // Handle global search
    const handleGlobalSearch = (value) => {
        setGlobalSearch(value);
    };

    // Clear all filters
    const clearAllFilters = () => {
        setFilters({
            miti: '',
            dakshila_no: '',
            specification: '',
            saman_ko_pahichan: '',
            model_no: '',
            item_name: '',
            utpadan_garnedesh: '',
            size: '',
            anumit_aayu: '',
            saman_prabidhik_srot: '',
            parikshan: '',
            prati_ekai_ko_dar: '',
            jamma_paral_mulya: '',
            pariman: '',
            jamma_paral_mulya2: '',
            pariman2: '',
            jamma_paral_mulya3: '',
            kaifiyat: ''
        });
        setGlobalSearch('');
    };

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Handle items per page change
    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    // Get page numbers
    const getPageNumbers = () => {
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        const pages = [];
        const maxVisiblePages = 5;
        
        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            }
        }
        
        return pages;
    };

    // Handle print
    const handlePrint = () => {
        // Create print-friendly HTML with exact table structure - EXACTLY matching the image
        const printHTML = `
            <html>
            <head>
                <title>Store Management Data - Verification Report</title>
                <style>
                    @media print {
                        body { margin: 0; padding: 20px; }
                        .print-header { text-align: center; font-size: 24px; font-weight: bold; margin-bottom: 20px; }
                        table { border-collapse: collapse; width: 100%; font-size: 12px; }
                        th, td { border: 1px solid #000; padding: 6px; text-align: center; vertical-align: middle; }
                        .main-header { background-color: #4a5568 !important; color: white !important; font-weight: bold; }
                        .sub-header { background-color: #e2e8f0 !important; font-weight: bold; }
                        .column-numbers { background-color: #90EE90 !important; font-weight: bold; }
                        .data-row:nth-child(even) { background-color: #90EE90 !important; }
                        .data-row:nth-child(odd) { background-color: white !important; }
                        .no-print { display: none !important; }
                    }
                    body { font-family: Arial, sans-serif; }
                    .print-header { text-align: center; font-size: 24px; font-weight: bold; margin-bottom: 20px; }
                    table { border-collapse: collapse; width: 100%; font-size: 12px; }
                    th, td { border: 1px solid #000; padding: 6px; text-align: center; vertical-align: middle; }
                    .main-header { background-color: #4a5568; color: white; font-weight: bold; }
                    .sub-header { background-color: #e2e8f0; font-weight: bold; }
                    .column-numbers { background-color: #90EE90; font-weight: bold; }
                    .data-row:nth-child(even) { background-color: #90EE90; }
                    .data-row:nth-child(odd) { background-color: white; }
                    .print-button { 
                        position: fixed; 
                        top: 20px; 
                        right: 20px; 
                        padding: 10px 20px; 
                        background: #4a5568; 
                        color: white; 
                        border: none; 
                        border-radius: 5px; 
                        cursor: pointer; 
                        font-size: 16px;
                    }
                    .print-button:hover { background: #2d3748; }
                    @media print { .print-button { display: none; } }
                </style>
            </head>
            <body>
                <button class="print-button" onclick="window.print()">🖨️ Print</button>
                <div class="print-header">Store Management Data - Verification Report</div>
                <table>
                    <thead>
                        <tr class="main-header">
                            <th rowspan="2">क्र.सं.</th>
                            <th rowspan="2">मिति</th>
                            <th rowspan="2">दाखिला न/ हस्तान्तरण नं.</th>
                            <th rowspan="2">स्पेसिफिकेसन</th>
                            <th rowspan="2">सामानको पहिचान नं</th>
                            <th rowspan="2">मोडल न</th>
                            <th rowspan="2">जिन्सी सामानको नाम</th>
                            <th colspan="4">विवरण</th>
                            <th colspan="3">आम्दानी</th>
                            <th colspan="2">हस्तान्तरण/मिन्हा/निःसर्ग</th>
                            <th colspan="2">बाँकी</th>
                            <th rowspan="2">कैफियत</th>
                            <th colspan="2">मूल्यह्रास</th>
                            <th rowspan="2">सत्यापित</th>
                            <th rowspan="2">टिप्पणी</th>
                        </tr>
                        <tr class="sub-header">
                            <th>उत्पादन गर्ने देश वा कम्पनीको नाम</th>
                            <th>साइज</th>
                            <th>अनुमानित आयु</th>
                            <th>सामान प्राप्तिको स्रोत</th>
                            <th>परिमाण</th>
                            <th>प्रति एकाइको दर (अन्य खर्चको समेतका आधारमा)</th>
                            <th>जम्मा परल मूल्य</th>
                            <th>परिमाण</th>
                            <th>जम्मा परल मूल्य</th>
                            <th>परिमाण</th>
                            <th>जम्मा परल मूल्य</th>
                            <th>मूल्यह्रास रकम</th>
                            <th>वर्तमान मूल्य</th>
                        </tr>
                        <tr class="column-numbers">
                            <th></th>
                            <th>१</th>
                            <th>२</th>
                            <th>३</th>
                            <th>४</th>
                            <th>५</th>
                            <th></th>
                            <th>६</th>
                            <th>७</th>
                            <th>८</th>
                            <th>९</th>
                            <th>१०</th>
                            <th>११</th>
                            <th>१२</th>
                            <th>१३</th>
                            <th>१४</th>
                            <th>१५</th>
                            <th>१६</th>
                            <th>१७</th>
                            <th>१८</th>
                            <th>१९</th>
                            <th>२०</th>
                            <th>२१</th>
                            <th>२२</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${filteredData.map((item, index) => {
                            const depreciation = calculateDepreciation(item);
                            const isVerified = verificationStates[item.id] || false;
                            const comment = verificationComments[item.id] || '';
                            
                            return `
                                <tr class="data-row">
                                    <td>${startIndex + index}</td>
                                    <td>${item.miti_nepali || item.miti || ''}</td>
                                    <td>${item.dakshila_no || ''}</td>
                                    <td>${item.specification || ''}</td>
                                    <td>${item.saman_ko_pahichan || ''}</td>
                                    <td>${item.model_no || ''}</td>
                                    <td>${item.item_name || ''}</td>
                                    <td>${item.utpadan_garnedesh || ''}</td>
                                    <td>${item.size || ''}</td>
                                    <td>${item.anumit_aayu || ''}</td>
                                    <td>${item.saman_prabidhik_srot || ''}</td>
                                    <td>${item.parikshan || ''}</td>
                                    <td>${item.prati_ekai_ko_dar || ''}</td>
                                    <td>${item.jamma_paral_mulya || ''}</td>
                                    <td>${item.pariman || ''}</td>
                                    <td>${item.jamma_paral_mulya2 || ''}</td>
                                    <td>${item.pariman2 || ''}</td>
                                    <td>${item.jamma_paral_mulya3 || ''}</td>
                                                                         <td>${item.kaifiyat || ''}</td>
                                     <td>${manualDepreciation[item.id]?.depreciationAmount || ''}</td>
                                     <td>${manualDepreciation[item.id]?.currentValue || ''}</td>
                                     <td>${isVerified ? '✅' : '❌'}</td>
                                    <td>${comment}</td>
                                </tr>
                            `;
                        }).join('')}
                    </tbody>
                </table>
            </body>
            </html>
        `;

        // Create a new window and write the HTML content
        const printWindow = window.open('', '_blank', 'width=1200,height=800');
        printWindow.document.write(printHTML);
        printWindow.document.close();
        
        // Focus the window but don't auto-print or close
        printWindow.focus();
    };

    // Download Excel
    const downloadExcel = () => {
        // Create HTML table with proper structure for Excel - EXACTLY matching the image
        const createExcelHTML = () => {
            const tableHTML = `
                <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
                <head>
                    <meta charset="UTF-8">
                    <style>
                        table { border-collapse: collapse; width: 100%; }
                        th, td { border: 1px solid #000; padding: 8px; text-align: center; vertical-align: middle; }
                        .main-header { background-color: #4a5568; color: white; font-weight: bold; }
                        .sub-header { background-color: #e2e8f0; font-weight: bold; }
                        .column-numbers { background-color: #90EE90; font-weight: bold; }
                        .data-row:nth-child(even) { background-color: #90EE90; }
                        .data-row:nth-child(odd) { background-color: white; }
                    </style>
                </head>
                <body>
                    <table>
                        <thead>
                            <tr class="main-header">
                                <th rowspan="2">क्र.सं.</th>
                                <th rowspan="2">मिति</th>
                                <th rowspan="2">दाखिला न/ हस्तान्तरण नं.</th>
                                <th rowspan="2">स्पेसिफिकेसन</th>
                                <th rowspan="2">सामानको पहिचान नं</th>
                                <th rowspan="2">मोडल न</th>
                                <th rowspan="2">जिन्सी सामानको नाम</th>
                                <th colspan="4">विवरण</th>
                                <th colspan="3">आम्दानी</th>
                                <th colspan="2">हस्तान्तरण/मिन्हा/निःसर्ग</th>
                                <th colspan="2">बाँकी</th>
                                <th rowspan="2">कैफियत</th>
                                <th colspan="2">मूल्यह्रास</th>
                                <th rowspan="2">सत्यापित</th>
                                <th rowspan="2">टिप्पणी</th>
                            </tr>
                            <tr class="sub-header">
                                <th>उत्पादन गर्ने देश वा कम्पनीको नाम</th>
                                <th>साइज</th>
                                <th>अनुमानित आयु</th>
                                <th>सामान प्राप्तिको स्रोत</th>
                                <th>परिमाण</th>
                                <th>प्रति एकाइको दर (अन्य खर्चको समेतका आधारमा)</th>
                                <th>जम्मा परल मूल्य</th>
                                <th>परिमाण</th>
                                <th>जम्मा परल मूल्य</th>
                                <th>परिमाण</th>
                                <th>जम्मा परल मूल्य</th>
                                <th>मूल्यह्रास रकम</th>
                                <th>वर्तमान मूल्य</th>
                            </tr>
                            <tr class="column-numbers">
                                <th></th>
                                <th>१</th>
                                <th>२</th>
                                <th>३</th>
                                <th>४</th>
                                <th>५</th>
                                <th></th>
                                <th>६</th>
                                <th>७</th>
                                <th>८</th>
                                <th>९</th>
                                <th>१०</th>
                                <th>११</th>
                                <th>१२</th>
                                <th>१३</th>
                                <th>१४</th>
                                <th>१५</th>
                                <th>१६</th>
                                <th>१७</th>
                                <th>१८</th>
                                <th>१९</th>
                                <th>२०</th>
                                <th>२१</th>
                                <th>२२</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${filteredData.map((item, index) => {
                                const depreciation = calculateDepreciation(item);
                                const isVerified = verificationStates[item.id] || false;
                                const comment = verificationComments[item.id] || '';
                                
                                return `
                                    <tr class="data-row">
                                        <td>${startIndex + index}</td>
                                        <td>${item.miti_nepali || item.miti || ''}</td>
                                        <td>${item.dakshila_no || ''}</td>
                                        <td>${item.specification || ''}</td>
                                        <td>${item.saman_ko_pahichan || ''}</td>
                                        <td>${item.model_no || ''}</td>
                                        <td>${item.item_name || ''}</td>
                                        <td>${item.utpadan_garnedesh || ''}</td>
                                        <td>${item.size || ''}</td>
                                        <td>${item.anumit_aayu || ''}</td>
                                        <td>${item.saman_prabidhik_srot || ''}</td>
                                        <td>${item.parikshan || ''}</td>
                                        <td>${item.prati_ekai_ko_dar || ''}</td>
                                        <td>${item.jamma_paral_mulya || ''}</td>
                                        <td>${item.pariman || ''}</td>
                                        <td>${item.jamma_paral_mulya2 || ''}</td>
                                        <td>${item.pariman2 || ''}</td>
                                        <td>${item.jamma_paral_mulya3 || ''}</td>
                                                                                 <td>${item.kaifiyat || ''}</td>
                                         <td>${manualDepreciation[item.id]?.depreciationAmount || ''}</td>
                                         <td>${manualDepreciation[item.id]?.currentValue || ''}</td>
                                         <td>${isVerified ? 'Yes' : 'No'}</td>
                                        <td>${comment}</td>
                                    </tr>
                                `;
                            }).join('')}
                        </tbody>
                    </table>
                </body>
                </html>
            `;
            return tableHTML;
        };

        // Create and download Excel file
        const htmlContent = createExcelHTML();
        const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'store_management_verification_data.xls';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Handle delete all
    const handleDeleteAll = async () => {
        if (deletePassword !== 'admin123') {
            setDeleteError('Incorrect password');
            return;
        }

        setIsDeleting(true);
        setDeleteError('');

        try {
            const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/items/delete-all/`, {
                method: 'DELETE',
                body: JSON.stringify({ password: deletePassword }),
            });

            if (response.ok) {
                setShowDeleteModal(false);
                setDeletePassword('');
                fetchStoreData();
            } else {
                const errorData = await response.json();
                setDeleteError(errorData.error || 'Failed to delete all items');
            }
        } catch (error) {
            setDeleteError('Error deleting items');
            console.error('Error:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    if (error) {
        return <div className={styles.error}>Error: {error}</div>;
    }

    // Calculate start index for pagination
    const startIndex = (currentPage - 1) * itemsPerPage + 1;

    return (
        <div className={styles.container}>
            {/* Header with controls */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h2>📋 Data Verification</h2>
                    <p>Verify and calculate depreciation for store items</p>
                </div>
                <div className={styles.headerRight}>
                    <button 
                        className={styles.refreshBtn}
                        onClick={() => {
                            setRefreshing(true);
                            fetchStoreData(false).finally(() => setRefreshing(false));
                        }}
                        disabled={refreshing}
                    >
                        {refreshing ? '🔄' : '🔄'} Refresh
                    </button>
                    <button className={styles.printBtn} onClick={handlePrint}>
                        🖨️ Print Report
                    </button>
                    <button className={styles.excelBtn} onClick={downloadExcel}>
                        📊 Export Excel
                    </button>
                    <button 
                        className={styles.deleteBtn}
                        onClick={() => setShowDeleteModal(true)}
                    >
                        🗑️ Delete All
                    </button>
                </div>
            </div>

            {/* Global Search */}
            <div className={styles.globalSearch}>
                <input
                    type="text"
                    placeholder="🔍 Search across all fields..."
                    value={globalSearch}
                    onChange={(e) => handleGlobalSearch(e.target.value)}
                    className={styles.searchInput}
                />
                <button 
                    className={styles.filterToggle}
                    onClick={() => setShowFilters(!showFilters)}
                >
                    {showFilters ? '🔽' : '🔼'} Filters
                </button>
                <button 
                    className={styles.clearFilters}
                    onClick={clearAllFilters}
                >
                    🗑️ Clear Filters
                </button>
            </div>

            {/* Filters Panel */}
            {showFilters && (
                <div className={styles.filtersPanel}>
                    <div className={styles.filtersGrid}>
                        <div className={styles.filterGroup}>
                            <label>मिति:</label>
                            <input
                                type="text"
                                value={filters.miti}
                                onChange={(e) => handleFilterChange('miti', e.target.value)}
                                placeholder="Filter by date..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>दक्षिणा नं.:</label>
                            <input
                                type="text"
                                value={filters.dakshila_no}
                                onChange={(e) => handleFilterChange('dakshila_no', e.target.value)}
                                placeholder="Filter by dakshila no..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>विनिर्देशन:</label>
                            <input
                                type="text"
                                value={filters.specification}
                                onChange={(e) => handleFilterChange('specification', e.target.value)}
                                placeholder="Filter by specification..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>सामानको पहिचान:</label>
                            <input
                                type="text"
                                value={filters.saman_ko_pahichan}
                                onChange={(e) => handleFilterChange('saman_ko_pahichan', e.target.value)}
                                placeholder="Filter by identification..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>मोडेल नं.:</label>
                            <input
                                type="text"
                                value={filters.model_no}
                                onChange={(e) => handleFilterChange('model_no', e.target.value)}
                                placeholder="Filter by model no..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>जिन्सी सामानको नाम:</label>
                            <input
                                type="text"
                                value={filters.item_name}
                                onChange={(e) => handleFilterChange('item_name', e.target.value)}
                                placeholder="Filter by item name..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>उत्पादन गर्ने देश:</label>
                            <input
                                type="text"
                                value={filters.utpadan_garnedesh}
                                onChange={(e) => handleFilterChange('utpadan_garnedesh', e.target.value)}
                                placeholder="Filter by country..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>साइज:</label>
                            <input
                                type="text"
                                value={filters.size}
                                onChange={(e) => handleFilterChange('size', e.target.value)}
                                placeholder="Filter by size..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>अनुमित आयु:</label>
                            <input
                                type="text"
                                value={filters.anumit_aayu}
                                onChange={(e) => handleFilterChange('anumit_aayu', e.target.value)}
                                placeholder="Filter by age..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>सामान प्राविधिक स्रोत:</label>
                            <input
                                type="text"
                                value={filters.saman_prabidhik_srot}
                                onChange={(e) => handleFilterChange('saman_prabidhik_srot', e.target.value)}
                                placeholder="Filter by source..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>परिक्षण:</label>
                            <input
                                type="text"
                                value={filters.parikshan}
                                onChange={(e) => handleFilterChange('parikshan', e.target.value)}
                                placeholder="Filter by inspection..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>प्रति एकाईको दर:</label>
                            <input
                                type="text"
                                value={filters.prati_ekai_ko_dar}
                                onChange={(e) => handleFilterChange('prati_ekai_ko_dar', e.target.value)}
                                placeholder="Filter by unit price..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>जम्मा पराल मूल्य:</label>
                            <input
                                type="text"
                                value={filters.jamma_paral_mulya}
                                onChange={(e) => handleFilterChange('jamma_paral_mulya', e.target.value)}
                                placeholder="Filter by total value..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>परिमाण:</label>
                            <input
                                type="text"
                                value={filters.pariman}
                                onChange={(e) => handleFilterChange('pariman', e.target.value)}
                                placeholder="Filter by quantity..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>जम्मा पराल मूल्य 2:</label>
                            <input
                                type="text"
                                value={filters.jamma_paral_mulya2}
                                onChange={(e) => handleFilterChange('jamma_paral_mulya2', e.target.value)}
                                placeholder="Filter by total value 2..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>परिमाण 2:</label>
                            <input
                                type="text"
                                value={filters.pariman2}
                                onChange={(e) => handleFilterChange('pariman2', e.target.value)}
                                placeholder="Filter by quantity 2..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>जम्मा पराल मूल्य 3:</label>
                            <input
                                type="text"
                                value={filters.jamma_paral_mulya3}
                                onChange={(e) => handleFilterChange('jamma_paral_mulya3', e.target.value)}
                                placeholder="Filter by total value 3..."
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>कैफियत:</label>
                            <input
                                type="text"
                                value={filters.kaifiyat}
                                onChange={(e) => handleFilterChange('kaifiyat', e.target.value)}
                                placeholder="Filter by remarks..."
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Data Table */}
            <div className={styles.tableContainer}>
                <table className={styles.dataTable}>
                    <thead>
                        <tr className={styles.mainHeader}>
                            <th rowSpan="2">क्र.सं.</th>
                            <th rowSpan="2">मिति</th>
                            <th rowSpan="2">दाखिला न/ हस्तान्तरण नं.</th>
                            <th rowSpan="2">स्पेसिफिकेसन</th>
                            <th rowSpan="2">सामानको पहिचान नं</th>
                            <th rowSpan="2">मोडल न</th>
                            <th rowSpan="2">जिन्सी सामानको नाम</th>
                            <th colSpan="4">विवरण</th>
                            <th colSpan="3">आम्दानी</th>
                            <th colSpan="2">हस्तान्तरण/मिन्हा/निःसर्ग</th>
                            <th colSpan="2">बाँकी</th>
                            <th rowSpan="2">कैफियत</th>
                            <th colSpan="2">मूल्यह्रास</th>
                            <th rowSpan="2">सत्यापित</th>
                            <th rowSpan="2">टिप्पणी</th>
                            <th rowSpan="2">कार्य</th>
                        </tr>
                        <tr className={styles.subHeader}>
                            <th>उत्पादन गर्ने देश वा कम्पनीको नाम</th>
                            <th>साइज</th>
                            <th>अनुमानित आयु</th>
                            <th>सामान प्राप्तिको स्रोत</th>
                            <th>परिमाण</th>
                            <th>प्रति एकाइको दर (अन्य खर्चको समेतका आधारमा)</th>
                            <th>जम्मा परल मूल्य</th>
                            <th>परिमाण</th>
                            <th>जम्मा परल मूल्य</th>
                            <th>परिमाण</th>
                            <th>जम्मा परल मूल्य</th>
                            <th title="Click to edit manually">मूल्यह्रास रकम ✏️</th>
                            <th title="Click to edit manually">वर्तमान मूल्य ✏️</th>
                        </tr>
                        <tr className={styles.columnNumbers}>
                            <th></th>
                            <th>१</th>
                            <th>२</th>
                            <th>३</th>
                            <th>४</th>
                            <th>५</th>
                            <th></th>
                            <th>६</th>
                            <th>७</th>
                            <th>८</th>
                            <th>९</th>
                            <th>१०</th>
                            <th>११</th>
                            <th>१२</th>
                            <th>१३</th>
                            <th>१४</th>
                            <th>१५</th>
                            <th>१६</th>
                            <th>१७</th>
                            <th>१८</th>
                            <th>१९</th>
                            <th>२०</th>
                            <th>२१</th>
                            <th>२२</th>
                        </tr>
                    </thead>
                    <tbody>
                                                 {filteredData.length === 0 ? (
                             <tr>
                                 <td colSpan="22" className={styles.noData}>
                                     कोही डाटा फेला परेन (No data found)
                                 </td>
                             </tr>
                        ) : (
                            filteredData.map((item) => {
                                const depreciation = calculateDepreciation(item);
                                const isVerified = verificationStates[item.id] || false;
                                const comment = verificationComments[item.id] || '';
                                
                                return (
                                    <tr key={item.id} className={styles.dataRow}>
                                        <td>{startIndex + filteredData.indexOf(item)}</td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'miti_nepali')}
                                            title="Click to verify this field"
                                        >
                                            {item.miti_nepali || item.miti || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'dakshila_no')}
                                            title="Click to verify this field"
                                        >
                                            {item.dakshila_no || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'specification')}
                                            title="Click to verify this field"
                                        >
                                            {item.specification || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'saman_ko_pahichan')}
                                            title="Click to verify this field"
                                        >
                                            {item.saman_ko_pahichan || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'model_no')}
                                            title="Click to verify this field"
                                        >
                                            {item.model_no || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'item_name')}
                                            title="Click to verify this field"
                                        >
                                            {item.item_name || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'utpadan_garnedesh')}
                                            title="Click to verify this field"
                                        >
                                            {item.utpadan_garnedesh || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'size')}
                                            title="Click to verify this field"
                                        >
                                            {item.size || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'anumit_aayu')}
                                            title="Click to verify this field"
                                        >
                                            {item.anumit_aayu || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'saman_prabidhik_srot')}
                                            title="Click to verify this field"
                                        >
                                            {item.saman_prabidhik_srot || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'parikshan')}
                                            title="Click to verify this field"
                                        >
                                            {item.parikshan || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'prati_ekai_ko_dar')}
                                            title="Click to verify this field"
                                        >
                                            {item.prati_ekai_ko_dar || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'jamma_paral_mulya')}
                                            title="Click to verify this field"
                                        >
                                            {item.jamma_paral_mulya || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'pariman')}
                                            title="Click to verify this field"
                                        >
                                            {item.pariman || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'jamma_paral_mulya2')}
                                            title="Click to verify this field"
                                        >
                                            {item.jamma_paral_mulya2 || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'pariman2')}
                                            title="Click to verify this field"
                                        >
                                            {item.pariman2 || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'jamma_paral_mulya3')}
                                            title="Click to verify this field"
                                        >
                                            {item.jamma_paral_mulya3 || ''}
                                        </td>
                                        <td 
                                            className={styles.clickableField}
                                            onClick={() => openFieldVerification(item, 'kaifiyat')}
                                            title="Click to verify this field"
                                        >
                                            {item.kaifiyat || ''}
                                        </td>
                                        <td className={styles.depreciationAmount}>
                                            <div className={styles.depreciationInputGroup}>
                                                <input
                                                    type="text"
                                                    value={manualDepreciation[item.id]?.depreciationAmount || ''}
                                                    onChange={(e) => {
                                                        // Auto-calculate current value when depreciation amount is typed
                                                        handleManualDepreciation(item.id, e.target.value, manualDepreciation[item.id]?.yearsDiff || 0, null);
                                                    }}
                                                    className={styles.depreciationInput}
                                                    placeholder="मूल्यह्रास रकम - manually type"
                                                    title="Click to edit manually - मूल्यह्रास रकम (auto-calculates वर्तमान मूल्य)"
                                                />
                                                {depreciation.isManual && (
                                                    <button
                                                        className={styles.resetBtn}
                                                        onClick={() => clearManualDepreciation(item.id)}
                                                        title="Reset to auto-calculated value"
                                                    >
                                                        🔄
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className={styles.depreciationValue}>
                                            <div className={styles.depreciationInputGroup}>
                                                <input
                                                    type="text"
                                                    value={manualDepreciation[item.id]?.currentValue || ''}
                                                    onChange={(e) => {
                                                        // Allow manual typing for current value
                                                        const newCurrentValue = e.target.value;
                                                        handleManualDepreciation(item.id, manualDepreciation[item.id]?.depreciationAmount || '', manualDepreciation[item.id]?.yearsDiff || 0, newCurrentValue);
                                                    }}
                                                    className={styles.depreciationInput}
                                                    placeholder="वर्तमान मूल्य - manually type"
                                                    title="Click to edit manually - वर्तमान मूल्य (or auto-calculated from मूल्यह्रास रकम)"
                                                />
                                                {depreciation.isManual && (
                                                    <button
                                                        className={styles.resetBtn}
                                                        onClick={() => clearManualDepreciation(item.id)}
                                                        title="Reset to auto-calculated value"
                                                    >
                                                        🔄
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                className={`${styles.verifyBtn} ${isVerified ? styles.verified : styles.unverified}`}
                                                onClick={() => handleVerificationCheck(item.id, !isVerified)}
                                                title={isVerified ? 'Mark as unverified' : 'Mark as verified'}
                                            >
                                                {isVerified ? '✅' : '❌'}
                                            </button>
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                value={comment}
                                                onChange={(e) => handleVerificationComment(item.id, e.target.value)}
                                                placeholder="Add comment..."
                                                className={styles.commentInput}
                                            />
                                        </td>
                                        <td>
                                            <button
                                                className={styles.viewDetailsBtn}
                                                onClick={() => openFieldVerification(item, 'all')}
                                                title="View all details"
                                            >
                                                👁️
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className={styles.pagination}>
                <div className={styles.paginationInfo}>
                    <span>
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} items
                    </span>
                    <select 
                        value={itemsPerPage} 
                        onChange={(e) => handleItemsPerPageChange(e)}
                        className={styles.itemsPerPage}
                    >
                        <option value={10}>10 per page</option>
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                        <option value={100}>100 per page</option>
                    </select>
                </div>
                <div className={styles.paginationControls}>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={styles.pageBtn}
                    >
                        ← Previous
                    </button>
                    {getPageNumbers().map((page, index) => (
                        <button
                            key={index}
                            onClick={() => typeof page === 'number' && handlePageChange(page)}
                            className={`${styles.pageBtn} ${page === currentPage ? styles.activePage : ''} ${typeof page !== 'number' ? styles.disabled : ''}`}
                            disabled={typeof page !== 'number'}
                        >
                            {page}
                        </button>
                    ))}
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
                        className={styles.pageBtn}
                    >
                        Next →
                    </button>
                </div>
            </div>

            {/* Delete Modal */}
            {showDeleteModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3>Delete All Items</h3>
                        <p>This action cannot be undone. Please enter the password to confirm.</p>
                        <input
                            type="password"
                            placeholder="Enter password"
                            value={deletePassword}
                            onChange={(e) => setDeletePassword(e.target.value)}
                            className={styles.passwordInput}
                        />
                        {deleteError && <p className={styles.error}>{deleteError}</p>}
                        <div className={styles.modalButtons}>
                            <button 
                                onClick={handleDeleteAll}
                                disabled={isDeleting}
                                className={styles.deleteConfirmBtn}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete All'}
                            </button>
                            <button 
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setDeletePassword('');
                                    setDeleteError('');
                                }}
                                className={styles.cancelBtn}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Field Verification Modal */}
            {showVerificationModal && selectedItem && (
                <div className={styles.modalOverlay}>
                    <div className={styles.verificationModal}>
                        <h3>Field Verification</h3>
                        <p>Item ID: {selectedItem.id}</p>
                        
                        {selectedField === 'all' ? (
                            <div className={styles.allFields}>
                                {Object.entries(selectedItem).map(([key, value]) => (
                                    <div key={key} className={styles.fieldRow}>
                                        <label>{key}:</label>
                                        <span>{value || 'N/A'}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.singleField}>
                                <label>{selectedField}:</label>
                                <span>{selectedItem[selectedField] || 'N/A'}</span>
                            </div>
                        )}
                        
                        <div className={styles.verificationActions}>
                            <button
                                className={`${styles.verifyBtn} ${verificationStates[selectedItem.id] ? styles.verified : styles.unverified}`}
                                onClick={() => handleVerificationCheck(selectedItem.id, !verificationStates[selectedItem.id])}
                            >
                                {verificationStates[selectedItem.id] ? '✅ Verified' : '❌ Unverified'}
                            </button>
                            <input
                                type="text"
                                value={verificationComments[selectedItem.id] || ''}
                                onChange={(e) => handleVerificationComment(selectedItem.id, e.target.value)}
                                placeholder="Add verification comment..."
                                className={styles.commentInput}
                            />
                        </div>
                        
                        <div className={styles.modalButtons}>
                            <button 
                                onClick={() => setShowVerificationModal(false)}
                                className={styles.closeBtn}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StoreVerify;
